(ns ui.view.entity-creator.opportunity.event
  (:require
   [common.localization :as loc]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.controller.websocket.event :as ws]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.view.modals.events :as modals]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [ui.cofx.util :as cofx]
   [ui.fx.toast :as toast]
   [ui.view.entity-creator.opportunity.model :as m]
   [ui.view.entity-creator.model :as creator-m]
   [ui.view.entity-creator.utils :as u]))

(defn initialize
  [{:keys [db]}
   [_ {:keys [ident stage pipeline entity-type initial-target target-locked?] :as _args}
    {:keys [on-ok! on-processed! close-modal!] :as _callbacks}]]
  (let [open-entity-fn (fn [entity-id]
                         (when (fn? close-modal!)
                           (close-modal!))
                         (routing/open-entity entity-id))
        new-db (cond-> (-> db
                           (assoc-in (m/stage-full-path ident) stage)
                           (assoc-in (m/pipeline-full-path ident) pipeline)
                           (assoc-in (m/entity-type-full-path ident) entity-type)
                           (assoc-in (m/open-entity-fn-full-path ident) open-entity-fn)
                           (assoc-in (m/callbacks-full-path ident)
                                     {:on-ok! on-ok!
                                      :on-processed! on-processed!
                                      :close-modal! close-modal!}))
                 initial-target
                 (-> (assoc-in (m/selected-target-full-path ident) initial-target)
                     (assoc-in (m/current-view-full-path ident) :entity-creator))
                 target-locked?
                 (assoc-in (m/target-locked?-full-path ident) true))]
    {:db new-db}))

(rf/reg-event-fx
 m/initialize
 []
 #'initialize)

(defn close-creator
  [{:keys [db]}
   [_ {:keys [ident] :as _args}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/i-root ident)))]
    {:db new-db}))

(rf/reg-event-fx
 m/close-creator
 []
 #'close-creator)

;; inject the selected target entity
(defn selected-target-entity-cofx
  [{:keys [original-event] :as cofx}]
  (let [[_ {:keys [target-entity-id target-entity-data target-type initial-value]
            :as _e-args}] original-event
        existing-target? (number? target-entity-id)
        person? (= target-type :entity.type/person)
        initial-attributes (when (and (not existing-target?)
                                      (keyword? target-type)
                                      (string? initial-value)
                                      (not (cu/blank-value? initial-value)))
                             (if person?
                               (u/try-parse-contact-info initial-value)
                               {(registry/resolve-name-attribute target-type)
                                (cu/str-trim initial-value)}))
        target-entity (cond existing-target?
                            ;; The caller passes the entity that it loaded from
                            ;; the backend (`entity-scope/load-entity!`). There
                            ;; is no session lookup here. The session storage
                            ;; is going away, and a session miss read as "no
                            ;; target chosen": the creator dropped the choice
                            ;; of the user without a message. The search row
                            ;; is not an entity either. A company row holds
                            ;; only its name.
                            (or target-entity-data
                                (logging/error
                                 "selected-target-entity-cofx: an existing target needs :target-entity-data, id"
                                 target-entity-id))

                            (keyword? target-type)
                            (cond-> {:db/id (str "new-" (name target-type))
                                     :entity/type target-type}
                              (and (string? initial-value)
                                   (not (cu/blank-value? initial-value)))
                              (merge initial-attributes)))]
    (cond-> cofx
      target-entity (assoc :target-entity target-entity))))

(rf/reg-cofx
 m/selected-target-entity-cofx
 #'selected-target-entity-cofx)

(defn target-load-failed
  "Tells the user that the selected target could not be loaded. The creator
   stays on the picker, so the user can select again."
  [_ _]
  {:fx [[::toast/error (loc/tr :creator/selection-load-failed)]]})

(rf/reg-event-fx
 m/target-load-failed
 []
 #'target-load-failed)

;; inject target-profile entity based on target
(defn target-profile-cofx
  [{:keys [original-event db] :as cofx}]
  (let [[_ {:keys [ident] :as _e-args}] original-event
        pipeline (get-in db (m/pipeline-full-path ident))
        entity-type (get-in db (m/entity-type-full-path ident))
        target (get-in db (m/selected-target-full-path ident))
        target-id (:db/id target)
        target-type (:entity/type target)
        target-profile? (ecu/target-profile?
                         {:pipeline pipeline
                          :entity-type entity-type
                          :target-type target-type})
        target-profile (when (and target-profile?
                                  (number? target-id))
                         (->> @session/investors
                              (cu/find-first-by :investor/legal-entity target-id)))]
    (cond-> cofx
      target-profile (assoc :target-profile target-profile))))

(rf/reg-cofx
 m/target-profile-cofx
 #'target-profile-cofx)

(defn select-target
  [{:keys [db target-entity]}
   [_ {:keys [ident] :as _args}]]
  (let [new-db (assoc-in db (m/selected-target-full-path ident) target-entity)]
    {:db new-db}))

(rf/reg-event-fx
 m/select-target
 [(rf/inject-cofx m/selected-target-entity-cofx)]
 #'select-target)

(defn set-creator-previous-target
  [{:keys [db]}
   [_ {:keys [ident] :as _args}]]
  (let [target (get-in db (m/selected-target-full-path ident))
        new-db (assoc-in db (m/creator-previous-target-full-path ident) target)]
    {:db new-db}))

(rf/reg-event-fx
 m/set-creator-previous-target
 []
 #'set-creator-previous-target)

(defn set-creator-previous-target-profile
  [{:keys [db target-profile]}
   [_ {:keys [ident] :as _args}]]
  (let [new-db (assoc-in db
                         (m/creator-previous-target-profile-full-path ident)
                         target-profile)]
    {:db new-db}))

(rf/reg-event-fx
 m/set-creator-previous-target-profile
 [(rf/inject-cofx m/target-profile-cofx)]
 #'set-creator-previous-target-profile)

(defn set-associations-target-type
  [{:keys [db]}
   [_ {:keys [ident] :as _args}]]
  (let [target (get-in db (m/selected-target-full-path ident))
        target-type (:entity/type target)
        new-db (assoc-in db (m/associations-target-type-full-path ident) target-type)]
    {:db new-db}))

(rf/reg-event-fx
 m/set-associations-target-type
 []
 #'set-associations-target-type)

;; note: currently investor is created during froppo-creation if it doesn't exist;
;;       consider creating it here: send request, set current-view in success-handler
(defn confirm-target
  [{:keys [db target-entity]}
   [_ {:keys [ident target-entity-id] :as args}]]
  (let [current-target (get-in db (m/selected-target-full-path ident))
        ;; click create, click back, click next:
        continuing-new? (and (not (nil? target-entity-id))
                             (= target-entity-id (:db/id current-target)))
        set-target? (and (or (contains? args :target-entity-id)
                             (contains? args :target-type))
                         (not continuing-new?))
        new-db (cond-> db
                 true (assoc-in (m/current-view-full-path ident) :entity-creator)
                 true (potpuri/dissoc-in (m/initial-target-value-full-path ident))
                 set-target? (assoc-in (m/selected-target-full-path ident) target-entity))]
    {:db new-db}))

(rf/reg-event-fx
 m/confirm-target
 [(rf/inject-cofx m/selected-target-entity-cofx)]
 #'confirm-target)

(defn resolve-association-entity-types
  [{:keys [db]}
   [_ {:keys [ident] :as _args}]]
  (let [pipeline (get-in db (m/pipeline-full-path ident))
        entity-type (get-in db (m/entity-type-full-path ident))
        target-entity (get-in db (m/selected-target-full-path ident))
        target-type (:entity/type target-entity)
        target-profile-type (ecu/resolve-target-profile-type
                             {:pipeline pipeline
                              :entity-type entity-type
                              :target-type target-type})]
    (cond-> #{entity-type}
      (keyword? target-type)
      (conj target-type)

      (keyword? target-profile-type)
      (conj target-profile-type))))

(defn confirm-entity
  [{:keys [db associations-config] :as params}
   [_ {:keys [ident] :as _args} :as event]]
  (let [stage (get-in db (m/stage-full-path ident))
        stage-id (:db/id stage)
        setting-path [:stages stage-id :setting]
        association-entity-types (resolve-association-entity-types params event)
        associations (->> associations-config
                          :associations
                          (filterv (fn [a]
                                     (contains? ecu/visible-settings
                                                (get-in a setting-path))))
                          (filterv (fn [{[source-entity-type _] :association-key}]
                                     (contains? association-entity-types
                                                source-entity-type))))
        next-view (if (seq associations)
                    :contact-details
                    :overview)
        new-db (-> db
                   (assoc-in (m/current-view-full-path ident) next-view))]
    {:db new-db}))

(rf/reg-event-fx
 m/confirm-entity
 [(rf/inject-cofx m/associations-config-cofx)]
 #'confirm-entity)

(defn confirm-contact-details
  [{:keys [db]}
   [_ {:keys [ident] :as _args}]]
  (let [new-db (assoc-in db (m/current-view-full-path ident) :overview)]
    {:db new-db}))

(rf/reg-event-fx
 m/confirm-contact-details
 []
 #'confirm-contact-details)

(defn previous-view
  [current-view]
  (case current-view
    :entity-creator :select-target-entity
    :target-creator :select-target-entity
    :contact-creator :contact-details
    :contact-details :entity-creator
    :overview :contact-details
    nil))

(defn navigate-back
  [{:keys [db associations-config] :as params}
   [_ {:keys [ident] :as _args} :as event]]
  (let [current-view (get-in db (m/current-view-full-path ident) :entity-creator)
        target-locked? (get-in db (m/target-locked?-full-path ident))
        locked-at-entity-creator? (and target-locked?
                                       (= current-view :entity-creator))
        stage (get-in db (m/stage-full-path ident))
        stage-id (:db/id stage)
        setting-path [:stages stage-id :setting]
        association-entity-types (resolve-association-entity-types params event)
        associations (->> associations-config
                          :associations
                          (filterv (fn [a]
                                     (contains? ecu/visible-settings
                                                (get-in a setting-path))))
                          (filterv (fn [{[source-entity-type _] :association-key}]
                                     (contains? association-entity-types
                                                source-entity-type))))
        update-fn (cond
                    locked-at-entity-creator?
                    identity

                    (and (= current-view :overview)
                         (empty? associations))
                    (constantly :entity-creator)

                    :else
                    previous-view)
        new-db (-> db
                   (update-in (m/current-view-full-path ident) update-fn)
                   (potpuri/dissoc-in (m/initial-target-value-full-path ident)))]
    {:db new-db}))

(rf/reg-event-fx
 m/navigate-back
 [(rf/inject-cofx m/associations-config-cofx)]
 #'navigate-back)

;; inject full entity based on entity-id
(defn associations-config-cofx
  [{:keys [original-event db] :as cofx}]
  (let [[_ {:keys [ident] :as _event-args}] original-event
        entity-type (get-in db (m/entity-type-full-path ident))
        target-entity (get-in db (m/selected-target-full-path ident))
        target-type (:entity/type target-entity)
        pipeline (get-in db (m/pipeline-full-path ident))
        stages (session/pipeline-stages pipeline)
        full-config (if pipeline
                      (:pipeline/creator-config pipeline)
                      (:tenant/entity-creation-config @session/tenant))
        config-path (:associations-config-path
                     (ecu/resolve-config-paths
                      {:entity-type entity-type
                       :target-type target-type
                       :pipeline pipeline}))
        raw-config (get-in full-config config-path)
        associations-config (ecu/resolve-associations-stage-config
                             {:config raw-config
                              :stages stages})]
    (cond-> cofx
      associations-config (assoc :associations-config associations-config))))

(rf/reg-cofx
 m/associations-config-cofx
 #'associations-config-cofx)

;; calculates from 'prefill-from' values a hashmap
;; {prefill-from [list-of-association-keys-to-prefill]}
(defn resolve-prefills
  [association-entity-types stage-id associations-config]
  (let [s-path (if stage-id
                 [:stages stage-id :setting]
                 [:create :setting])
        vals-by-prefill (->> associations-config
                             :associations
                             (filterv (fn [a-config]
                                        (let [setting (get-in a-config s-path)]
                                          (contains? ecu/visible-settings
                                                     setting))))
                             (filterv (fn [{[source-entity-type _] :association-key}]
                                        (contains? association-entity-types
                                                   source-entity-type)))
                             (filterv (fn [{:keys [prefill-from]}]
                                        prefill-from))
                             (mapv (fn [{:keys [association-key prefill-from]}]
                                     {:source prefill-from :target association-key}))
                             (group-by :source))]
    (update-vals vals-by-prefill (partial mapv :target))))

(defn explicit-nil?
  [edits prefill-target]
  (let [value (get-in edits prefill-target)]
    (and (nil? value)
         (contains? (get-in edits (butlast prefill-target))
                    (last prefill-target)))))

(defn prefill-all
  [{:keys [prefills prefill-sources edits current-prefills]}]
  (let [[prefill-source & prefill-sources-rest] prefill-sources
        current-prefills (or current-prefills #{})
        value (get-in edits prefill-source)
        prefill-targets (->> (get prefills prefill-source)
                             ;; prefill only associations that are not set
                             ;; (also avoids infinite loops from circular prefill rules)
                             (filterv (fn [prefill-target]
                                        (let [current-val (get-in edits prefill-target)
                                              prefilled? (contains? current-prefills
                                                                    prefill-target)]
                                          (or (nil? current-val)
                                              prefilled?))))
                             (filter (fn [prefill-target]
                                       ;; don't update association if explicitly set nil
                                       (not (explicit-nil? edits prefill-target)))))
        updated-edits (->> prefill-targets
                           (reduce (fn [edits association-key]
                                     (if (nil? value)
                                       (potpuri/dissoc-in edits association-key)
                                       (assoc-in edits association-key
                                                 value)))
                                   edits))
        new-current-prefills (->> prefill-targets
                                  (reduce (fn [cur-prefills association-key]
                                            (if (nil? value)
                                              (disj cur-prefills association-key)
                                              (conj cur-prefills association-key)))
                                          current-prefills))
        new-prefill-sources (-> (into (vec prefill-sources-rest) prefill-targets)
                                distinct
                                vec)]
    (if (empty? new-prefill-sources)
      [updated-edits
       new-current-prefills]
      (recur {:prefills prefills
              :prefill-sources new-prefill-sources
              :current-prefills new-current-prefills
              :edits updated-edits}))))

(defn set-association
  [{:keys [db associations-config] :as params}
   [_ {:keys [ident association-key val] :as _args} :as event]]
  (let [stage (-> db (get-in (m/stage-full-path ident)))
        val (if (empty? val)
              nil
              val)
        stage-id (:db/id stage)
        association-entity-types (resolve-association-entity-types params event)
        prefills (->> associations-config
                      (resolve-prefills association-entity-types stage-id))
        a-edits (get-in db (m/associations-edits-full-path ident))
        associations-edits (assoc-in a-edits association-key val)
        current-prefills (get-in db (m/current-association-prefills-full-path ident))
        current-prefills (or current-prefills #{})
        ;; explicitly editing => association-key will no longer be considered
        ;; as a prefilling target:
        current-prefills (disj current-prefills association-key)
        [new-edits
         new-current-prefills] (prefill-all {:prefills prefills
                                             :edits associations-edits
                                             :current-prefills current-prefills
                                             :prefill-sources [association-key]})
        current-view (get-in db (m/current-view-full-path ident))
        ;; if creating a new contact, move back and empty nested-on-ok
        creator? (= current-view :contact-creator)
        new-db (cond-> (assoc-in db (m/associations-edits-full-path ident) new-edits)
                 true (assoc-in (m/current-association-prefills-full-path ident)
                                new-current-prefills)
                 creator? (assoc-in (m/current-view-full-path ident) :contact-details)
                 creator? (potpuri/dissoc-in (m/nested-on-ok-full-path ident)))]
    {:db new-db}))

(rf/reg-event-fx
 m/set-association
 [(rf/inject-cofx m/associations-config-cofx)]
 #'set-association)

(defn reset-associations
  [{:keys [db]}
   [_ {:keys [ident] :as _args}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/associations-edits-full-path ident))
                   (potpuri/dissoc-in (m/current-association-prefills-full-path ident)))]
    {:db new-db}))

(rf/reg-event-fx
 m/reset-associations
 []
 #'reset-associations)

(defn set-nested-creator
  [{:keys [db]}
   [_ {:keys [ident target-type initial-value creator-type nested-on-ok!] :as _args}]]
  ;;note: no longer :target-creator step (target-fields included in oppo-creation);
  ;; only used for (associated) contact creation:
  (let [creator-type (or creator-type :target-creator)
        new-db (cond-> (-> db
                           (assoc-in (m/target-creator-type-full-path ident) target-type)
                           (assoc-in (m/current-view-full-path ident) creator-type))
                 (= creator-type :target-creator) (assoc-in
                                                   (m/target-creator-type-full-path ident)
                                                   target-type)
                 initial-value (assoc-in (m/initial-target-value-full-path ident)
                                         initial-value)
                 (fn? nested-on-ok!) (assoc-in (m/nested-on-ok-full-path ident)
                                               nested-on-ok!)
                 (not (fn? nested-on-ok!)) (potpuri/dissoc-in (m/nested-on-ok-full-path ident)))]
    {:db new-db}))

(rf/reg-event-fx
 m/set-nested-creator
 []
 #'set-nested-creator)

(defn start-create
  [{:keys [db tenant-schema default-currency now associations-config target-profile] :as params}
   [_ {:keys [ident creator-args] :as args} :as event]]
  (let [entity-type (-> db
                        (get-in (m/entity-type-full-path ident)))
        entity-edits (-> db
                         (get-in (creator-m/entity-edits-full-path
                                  (:ident creator-args))))
        entity-schema (registry/get-schema tenant-schema entity-type)
        ;;note: ensure contains pipeline, stage, investor, etc
        opportunity (u/normalize-entity-edits
                     {:entity-schema entity-schema
                      :default-currency default-currency}
                     (assoc entity-edits :entity/type entity-type))
        data {:opportunity opportunity}
        association-edits (-> db
                              (get-in (m/associations-edits-full-path ident)))
        association-entity-types (resolve-association-entity-types params event)
        associations (->> associations-config
                          :associations
                          (filterv (fn [{[source-entity-type _] :association-key}]
                                     (contains? association-entity-types
                                                source-entity-type)))
                          (keep (fn [{:keys [association-key]}]
                                  (let [val (get-in association-edits association-key)]
                                    (when val
                                      [association-key val]))))
                          (into {}))
        target (-> db
                   (get-in (m/selected-target-full-path ident)))
        target-id (:db/id target)
        target-type (:entity/type target)
        has-target? (boolean target-id)

        target-schema (when target-type
                        (registry/get-schema tenant-schema target-type))
        target-edits (when has-target?
                       (u/normalize-entity-edits
                        {:entity-schema target-schema
                         :default-currency default-currency}
                        (-> db
                            (get-in (creator-m/entity-edits-full-path
                                     (m/resolve-target-creator-ident args)))
                            (assoc :db/id target-id
                                   :entity/type target-type))))

        target-profile-id (:db/id target-profile)
        target-profile-edits (-> db
                                 (get-in (creator-m/entity-edits-full-path
                                          (m/resolve-target-profile-creator-ident args))))
        target-profile-type (-> db
                                (get-in (creator-m/entity-type-full-path
                                         (m/resolve-target-profile-creator-ident args))))
        pipeline (-> db
                     (get-in (m/pipeline-full-path ident)))
        target-profile? (ecu/target-profile?
                         {:pipeline pipeline
                          :entity-type entity-type
                          :target-type target-type})
        has-target-profile? (and target-profile?
                                 has-target?
                                 (contains? #{:entity.type/company
                                              :entity.type/person} target-type))
        target-profile-schema (when target-profile-type
                                (registry/get-schema tenant-schema target-profile-type))
        target-profile-edits (when has-target-profile?
                               (u/normalize-entity-edits
                                {:entity-schema target-profile-schema
                                 :default-currency default-currency}
                                (-> target-profile-edits
                                    (assoc :db/id (or target-profile-id
                                                      "new-target-profile")
                                           :entity/type target-profile-type))))
        add-legal-entity? (and has-target-profile?
                               (= target-profile-type :entity.type/investor)
                               (string? target-id))
        target-profile-edits (if add-legal-entity?
                               (assoc target-profile-edits
                                      :investor/legal-entity target-id)
                               target-profile-edits)
        data (cond-> data
               (seq associations) (assoc :associations associations)
               (not (cu/empty-value? target-edits)) (assoc :target target-edits)
               has-target-profile? (assoc :target-profile target-profile-edits))
        new-db (-> db
                   (assoc-in (m/processing-full-path ident) true)
                   (assoc-in (m/processing-start-time-full-path ident) now))]
    {:db new-db
     ::ws/send
     {:id :entity/create-opportunity-entity
      :body data
      :on-success [m/create-success args]
      :on-error [m/create-error args]}}))

(rf/reg-event-fx
 m/start-create
 [(rf/inject-cofx cofx/tenant-schema-cofx)
  (rf/inject-cofx cofx/default-currency-cofx)
  (rf/inject-cofx cofx/now-cofx)
  (rf/inject-cofx m/associations-config-cofx)
  (rf/inject-cofx m/target-profile-cofx)]
 #'start-create)

(defn create-error
  [{:keys [db]} [_ {:keys [ident] :as _args} error-response]]
  (let [{:keys [error message]} error-response
        msg (cond (string? message) message
                  (keyword? error) (loc/tr error)
                  :else (loc/tr :unknown-error-persists))
        new-db (-> db
                   (potpuri/dissoc-in (m/processing-full-path ident)))]
    {:db new-db
     :fx [[::modals/open-modal (partial u/open-error-modal! msg)]]}))

(rf/reg-event-fx
 m/create-error
 []
 #'create-error)

(defn create-success
  [{:keys [db now]} [_ {:keys [ident] :as args} success-response]]
  (let [callbacks (get-in db (m/callbacks-full-path ident))
        processing-start-time (get-in db (m/processing-start-time-full-path ident))
        {:keys [on-processed!]} callbacks
        new-entity (:body success-response)
        keep-processing? (fn? on-processed!)

        ;; m/processing-done immediately if no on-processed!,
        ;; else on-processed! :close-cb should be called with dispatch m/processing-done
        after-processed! (fn [_]
                           (js/setTimeout
                            (fn []
                              (rf/dispatch [m/processing-done args]))
                            (u/resolve-processing-time processing-start-time now)))
        callback (when keep-processing?
                   (partial on-processed!
                            {:new-entity-id (:db/id new-entity)
                             :new-entity    new-entity
                             ;; See ui.view.entity-creator.event/create-success -
                             ;; the handle for the off-thread search indexing,
                             ;; forwarded on the same contract so both creator
                             ;; flows hand :on-processed! the same map.
                             :completion-id (:completion/id success-response)
                             :close-cb!     after-processed!}))
        new-db (-> db
                   (assoc-in (m/created-entity-full-path ident) new-entity))]
    {:db new-db
     :fx [(if-not keep-processing?
            [:dispatch-later
             {:ms (u/resolve-processing-time processing-start-time now)
              :dispatch [m/processing-done args]}]
            [m/callback-fx callback])]}))

(rf/reg-event-fx
 m/create-success
 [(rf/inject-cofx cofx/now-cofx)]
 #'create-success)

(defn processing-done
  [{:keys [db]} [_ {:keys [ident skip-success-msg?] :as args}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/processing-full-path ident)))]
    {:db new-db
     :fx [(when skip-success-msg?
            [:dispatch [m/finish args]])]}))

(rf/reg-event-fx
 m/processing-done
 []
 #'processing-done)

(defn finish
  [{:keys [db]} [_ {:keys [ident] :as args}]]
  (let [callbacks (get-in db (m/callbacks-full-path ident))
        {:keys [on-ok! close-modal!]} callbacks
        new-entity (-> db
                       (get-in (m/created-entity-full-path ident)))
        ok! (if (fn? on-ok!)
              (partial on-ok! {:new-entity-id (:db/id new-entity)
                               :new-entity    new-entity
                               :close-cb!     close-modal!})
              close-modal!)]
    {:fx [[:dispatch [m/close-creator args]]
          [m/callback-fx ok!]]}))

(rf/reg-event-fx
 m/finish
 []
 #'finish)

(rf/reg-fx
 m/callback-fx
 (fn [callback]
   ;; rf/dispatch-sync not allowed in rf-event-handlers
   ;; => setTimeout as workaround:
   (js/setTimeout callback 0)))
